import React, { FunctionComponent, HTMLAttributeAnchorTarget, useRef, useState } from 'react';

import { Link } from 'react-router-dom';

import { Button as MuiButton, IconButton as MuiIconButton, Tooltip as MuiTooltip, Badge } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

import { ContextMenu, ProgressSpinner } from '@luxon/components';
import { IMenuItem, TColor } from '@luxon/interfaces';
import { IFormInputBase } from '@luxon/components/core/form/Form';

interface IButtonProps extends IFormInputBase {
    icon?: JSX.Element;
    iconPosition?: 'start' | 'end'
    iconOnly?: boolean;
    loading?: boolean;
    href?: string;
    hrefReplace?: boolean;
    hrefTarget?: HTMLAttributeAnchorTarget;
    className?: string;
    color?: 'inherit' | TColor;
    autoFocus?: boolean;
    ref?: React.ForwardedRef<HTMLButtonElement>;
    isFileUpload?: boolean;
    allowedFileTypes?: string;

    menuButtonOptions?: IMenuItem[];
    menuButtonOptionClicked?: (option: IMenuItem) => void;
    menuButtonOptionsHideDisabled?: boolean;

    type?: 'button' | 'submit' | 'reset';

    /**
     * Return true to clear the input, otherwise false
     */
    onFileUploaded?: (file: File) => void;

    variant?: 'text' | 'outlined' | 'contained';
    size?: 'small' | 'medium' | 'large';

    tooltip?: string;
    tooltipPlacement?: 'bottom-end' | 'bottom-start' | 'bottom' | 'left-end' | 'left-start' | 'left' | 'right-end' | 'right-start' | 'right' | 'top-end' | 'top-start' | 'top';

    children?: any;
    ariaLabel?: string;
    style?: any;
    enableWrapping?: boolean;

    badgeCounter?: number;
    badgeColor?: 'primary' | 'secondary' | 'success' | 'error' | 'warning';
    badgeAlignment?: 'start' | 'end';
}
export const Button: FunctionComponent<IButtonProps> = React.forwardRef((props: IButtonProps, ref) => {

    const [buttonMenuAnchorEl, setButtonMenuAnchorEl] = useState<HTMLElement>(null);

    const handleMenuItemClicked = (option: IMenuItem) => {
        setButtonMenuAnchorEl(null);
        if (props.menuButtonOptionClicked) {
            props.menuButtonOptionClicked(option);
        }
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (props.menuButtonOptions?.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            setButtonMenuAnchorEl(e.currentTarget as HTMLElement);
        } else if (!props.isFileUpload && props.onClick) {
            props.onClick(e);
        } else if (props.isFileUpload) {
            inputRef.current.click();
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (props.onFileUploaded && e.target.files.length > 0) {
            props.onFileUploaded(e.target.files[0]);
        }

        inputRef.current.value = null;
    }

    const icon: JSX.Element | undefined = props.loading
        ? (<ProgressSpinner size={20} />)
        : props.icon;

    const inputRef = useRef<HTMLInputElement>();
    const fileUploadInput = !props.isFileUpload ? null : (
        <input type='file' ref={inputRef} hidden accept={props.allowedFileTypes} onChange={handleFileChange} />
    );

    const wrappedButtonContent = (children: JSX.Element) => {
        return props.badgeCounter ? (
            <Badge
                color={props.badgeColor ?? 'primary'}
                badgeContent={props.badgeCounter}
                max={99}
                sx={{
                    width: '100%'
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: props.badgeAlignment === 'start' ? 'left' : 'right'
                }}
                slotProps={{
                    badge: {
                        style: {
                            right: props.badgeAlignment === 'start' ? undefined : '3px',
                            left: props.badgeAlignment === 'start' ? '3px' : undefined,
                            top: '3px'
                        }
                    }
                }}
            >
                {children}
            </Badge>
        ) : children;
    }

    const button = props.iconOnly && props.icon ? (
        <MuiIconButton
            disabled={props.disabled}
            type={props.type ?? 'button'}
            size={props.size ?? 'medium'}
            onClick={handleClick}
            className={props.className}
            color={props.color}
            autoFocus={props.autoFocus}
            ref={ref}
            style={props.style}
            aria-label={props.ariaLabel}
        >
            {icon}
            {fileUploadInput}
        </MuiIconButton>
    ) : (
        <MuiButton
            startIcon={props.iconPosition !== 'end' ? icon : null}
            endIcon={props.iconPosition === 'end' && icon ? icon : props.menuButtonOptions?.length > 0 ? <KeyboardArrowDown />  : null}
            disabled={props.loading || props.disabled}
            type={props.type ?? 'button'}
            variant={props.variant ?? 'contained'}
            size={props.size ?? 'medium'}
            onClick={handleClick}
            className={props.className}
            color={props.color}
            autoFocus={props.autoFocus}
            ref={ref}
            style={props.style}
            aria-label={props.ariaLabel}
            sx={props.enableWrapping ? {
                whiteSpace: 'normal'
            }: null}
        >
            {props.children}
            {fileUploadInput}
        </MuiButton>
    );

    const wrappedButton = props.tooltip ? (
        <MuiTooltip title={props.tooltip} placement={props.tooltipPlacement}>
            {wrappedButtonContent(button)}
        </MuiTooltip>
    ) : wrappedButtonContent(button);

    if (props.href && !props.disabled) {
        if (props.href.startsWith('http')) {
            return (
                <a href={props.href} target={props.hrefTarget} rel={props.hrefTarget === '_blank' ? 'noreferrer noopener' : null}>
                    {wrappedButton}
                </a>
            )
        }
        return (
            <Link to={props.href} replace={props.hrefReplace} target={props.hrefTarget} rel={props.hrefTarget === '_blank' ? 'noreferrer noopener' : null}>
                {wrappedButton}
            </Link>
        )
    }

    return (
        <>
            {wrappedButton}
            {
                props.menuButtonOptions?.length > 0 && (
                    <ContextMenu
                        anchorElement={buttonMenuAnchorEl}
                        onClose={() => setButtonMenuAnchorEl(null)}
                        hideDisabledItems={props.menuButtonOptionsHideDisabled}
                        options={props.menuButtonOptions}
                        onOptionSelected={handleMenuItemClicked}
                    />
                )
            }
        </>
    );
});